import type { ReactNode, SVGProps } from 'react';

/**
 * Hand-drawn equivalents of the subset of @actual-app/components/icons that
 * these screens use. Upstream generates ~730 components with SVGR; only the
 * ~30 marks that actually appear are reproduced here, on the same 24x24
 * stroke grid so weights match.
 */
export type IconProps = SVGProps<SVGSVGElement> & {
  width?: number;
  height?: number;
};

function icon(children: ReactNode, filled = false) {
  return function Icon({ width = 18, height = 18, ...rest }: IconProps) {
    return (
      <svg
        viewBox="0 0 24 24"
        width={width}
        height={height}
        fill={filled ? 'currentColor' : 'none'}
        stroke={filled ? 'none' : 'currentColor'}
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
        {...rest}
      >
        {children}
      </svg>
    );
  };
}

export const SvgWallet = icon(
  <>
    <path d="M3 7.5A2.5 2.5 0 0 1 5.5 5H18a2 2 0 0 1 2 2v1" />
    <rect x="3" y="7.5" width="18" height="12" rx="2.2" />
    <circle cx="16.5" cy="13.5" r="1.2" fill="currentColor" stroke="none" />
  </>,
);

export const SvgReports = icon(
  <>
    <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
  </>,
);

export const SvgCalendar3 = icon(
  <>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </>,
);

export const SvgCalendar = icon(
  <>
    <rect x="3.5" y="5.5" width="17" height="15" rx="2" />
    <path d="M3.5 10h17M8 3.5v4M16 3.5v4" />
  </>,
);

export const SvgCog = icon(
  <>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 2.8v2.4M12 18.8v2.4M4.6 7.6l2 1.2M17.4 15.2l2 1.2M4.6 16.4l2-1.2M17.4 8.8l2-1.2" />
  </>,
);

export const SvgTuning = icon(
  <>
    <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
    <circle cx="16" cy="7" r="2" />
    <circle cx="10" cy="17" r="2" />
  </>,
);

export const SvgStoreFront = icon(
  <>
    <path d="M4 9V6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5V9" />
    <path d="M3 9h18l-1 2.2a2.4 2.4 0 0 1-4.4.3 2.4 2.4 0 0 1-4.3 0 2.4 2.4 0 0 1-4.3 0A2.4 2.4 0 0 1 4 11.2L3 9Z" />
    <path d="M5 13.5V19h14v-5.5" />
  </>,
);

export const SvgTag = icon(
  <>
    <path d="M12.6 3.6H20V11l-8.6 8.6a2 2 0 0 1-2.8 0L3.8 14.8a2 2 0 0 1 0-2.8L12.6 3.6Z" />
    <circle cx="16.4" cy="7.4" r="1.2" fill="currentColor" stroke="none" />
  </>,
);

export const SvgCreditCard = icon(
  <>
    <rect x="2.5" y="5.5" width="19" height="13" rx="2.2" />
    <path d="M2.5 10h19M6 15h4" />
  </>,
);

export const SvgAdd = icon(<path d="M12 5v14M5 12h14" />);

export const SvgCheveronDown = icon(<path d="M6 9.5 12 15l6-5.5" />);
export const SvgCheveronUp = icon(<path d="M6 14.5 12 9l6 5.5" />);
export const SvgCheveronRight = icon(<path d="M9.5 6 15 12l-5.5 6" />);
export const SvgCheveronLeft = icon(<path d="M14.5 6 9 12l5.5 6" />);

export const SvgArrowRight = icon(<path d="M4 12h15m-5-5 5 5-5 5" />);
export const SvgArrowLeft = icon(<path d="M20 12H5m5-5-5 5 5 5" />);

export const SvgRefresh = icon(
  <>
    <path d="M20 11a8 8 0 1 0-.7 4.4" />
    <path d="M20 5v6h-6" />
  </>,
);

export const SvgArrowsSynchronize = icon(
  <>
    <path d="M4 8h13m-3.5-3.5L17 8l-3.5 3.5M20 16H7m3.5-3.5L7 16l3.5 3.5" />
  </>,
);

export const SvgEyeOpen = icon(
  <>
    <path d="M2.5 12S6 5.8 12 5.8 21.5 12 21.5 12 18 18.2 12 18.2 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="2.8" />
  </>,
);

export const SvgEyeClosed = icon(
  <>
    <path d="M4 6.5 20 18M20 6.5 4 18" />
    <path d="M2.8 12S6.3 6.2 12 6.2c1.4 0 2.6.3 3.7.8M21.2 12s-3.5 5.8-9.2 5.8c-1.4 0-2.7-.3-3.8-.8" />
  </>,
);

export const SvgHelp = icon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.6 9.3a2.5 2.5 0 1 1 3.6 2.3c-.8.5-1.2.9-1.2 1.9" />
    <circle cx="12" cy="16.7" r=".9" fill="currentColor" stroke="none" />
  </>,
);

export const SvgDotsHorizontal = icon(
  <>
    <circle cx="5.5" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="18.5" cy="12" r="1.5" fill="currentColor" stroke="none" />
  </>,
);

export const SvgDotsVertical = icon(
  <>
    <circle cx="12" cy="5.5" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="12" cy="18.5" r="1.5" fill="currentColor" stroke="none" />
  </>,
);

export const SvgSearch = icon(
  <>
    <circle cx="10.8" cy="10.8" r="6.3" />
    <path d="m15.5 15.5 4 4" />
  </>,
);

export const SvgDownload = icon(
  <>
    <path d="M12 3.5v11m0 0 4-4m-4 4-4-4M4 19h16" />
  </>,
);

export const SvgFilter = icon(<path d="M4 5.5h16l-6.2 7.3V19L10.2 20v-7.2L4 5.5Z" />);

export const SvgCheck = icon(<path d="m5 12.8 4.5 4.4L19 7.5" />);

export const SvgCheckCircle = icon(
  <>
    <circle cx="12" cy="12" r="9" fill="currentColor" stroke="none" />
    <path d="m8 12.2 2.8 2.8L16 9.6" stroke="#fff" />
  </>,
  true,
);

export const SvgSquare = icon(<rect x="4.5" y="4.5" width="15" height="15" rx="2.5" />);

export const SvgAlert = icon(
  <>
    <path d="M12 4.2 21 19.2H3L12 4.2Z" />
    <path d="M12 10v4" />
    <circle cx="12" cy="16.6" r=".9" fill="currentColor" stroke="none" />
  </>,
);

export const SvgFlag = icon(
  <>
    <path d="M6 21V4m0 0h11l-2 4 2 4H6" />
  </>,
);

export const SvgLockClosed = icon(
  <>
    <rect x="5" y="10.5" width="14" height="9.5" rx="2" />
    <path d="M8.2 10.5V8a3.8 3.8 0 0 1 7.6 0v2.5" />
  </>,
);

export const SvgPencil = icon(
  <>
    <path d="M4 20h4L19.2 8.8a2.1 2.1 0 0 0-3-3L5 17v3Z" />
    <path d="m14.5 6.5 3 3" />
  </>,
);

export const SvgTrash = icon(
  <>
    <path d="M4.5 7h15M9.5 7V4.8h5V7M6.5 7l1 13h9l1-13" />
  </>,
);

export const SvgExpand = icon(<path d="M9 4.5H4.5V9M15 19.5h4.5V15M4.5 15v4.5H9M19.5 9V4.5H15" />);

export const SvgCollapse = icon(<path d="M4.5 9H9V4.5M19.5 15H15v4.5M15 4.5V9h4.5M9 19.5V15H4.5" />);

export const SvgViewGrid = icon(
  <>
    <rect x="4" y="4" width="7" height="7" rx="1.5" />
    <rect x="13" y="4" width="7" height="7" rx="1.5" />
    <rect x="4" y="13" width="7" height="7" rx="1.5" />
    <rect x="13" y="13" width="7" height="7" rx="1.5" />
  </>,
);

export const SvgTransfer = icon(
  <>
    <path d="M7 7h11m0 0-3-3m3 3-3 3M17 17H6m0 0 3-3m-3 3 3 3" />
  </>,
);

export const SvgInfo = icon(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5.5" />
    <circle cx="12" cy="7.9" r=".95" fill="currentColor" stroke="none" />
  </>,
);

export const SvgBills = icon(
  <>
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <path d="M8 9h8M8 13h8M8 17h5" />
  </>,
);

export const SvgLoading = icon(
  <>
    <path d="M12 3.5v4M12 16.5v4M3.5 12h4M16.5 12h4M6 6l2.8 2.8M15.2 15.2 18 18M18 6l-2.8 2.8M8.8 15.2 6 18" />
  </>,
);

export const SvgUndelete = icon(
  <>
    <path d="M4 12a8 8 0 1 0 2.4-5.7" />
    <path d="M4 4v4h4" />
  </>,
);
